<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="90vh")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('edit_appointment_details') }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

        template(v-else-if="appointment")
          appointment-details(ref="details" :appointment="appointment")
            template(#title)
              div.text-md.font-semibold.neutral700--text {{ $t('make_change_on_details') }}

      v-divider
      v-card-actions.py-4(:class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0(@click="closeDialog" text) {{ $t('cancel') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" color="neutral700" depressed :dark="hasChanges" :disabled="!hasChanges" @click="saveData") {{ $t('save') }}

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AppointmentDetails from '@/components/clinic/appointment/AppointmentDetails.vue'

export default {
  name: 'EditAppointmentDetails',
  components: { AppointmentDetails },
  props: ['open', 'selectedAppointment', 'skipReset'],
  data () {
    return {
      isFetching: false,
      isLoading: false,
      appointment: null,
      initialData: null,
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      tempAppointment: 'clinic/getTempAppointment',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    hasChanges () {
      if (this.tempAppointment && this.tempAppointment.form) {
        return JSON.stringify(this.initialData) !== JSON.stringify(this.tempAppointment.form)
      }
      return false
    }
  },
  mounted () {
    this.fetchAppointment()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchAppointment () {
      this.isFetching = true
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
          this.initialData = {
            service: this.appointment.service.id,
            provider: this.appointment.provider.id,
            date: this.appointment.date,
            time: this.appointment.time,
            remarks: this.appointment.lead.remarks,
          }
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    saveData () {
      if (!this.$refs.details.$refs.form.validate()) {
        return
      }
      if (!this.tempAppointment) {
        return
      }
      this.isLoading = true
      let data = {
        lead: { remarks: this.tempAppointment.form.remarks },
      }
      if (this.appointment.type === 'regular') {
        data.service = this.tempAppointment.form.service
        data.provider = this.tempAppointment.form.provider
      }
      if (this.appointment.type !== 'combo' && this.appointment.services.length <= 1) {
        data.start = `${this.tempAppointment.form.date} ${this.tempAppointment.form.time}`
      }
      if (this.tempAppointment.override) {
        data.ignore_rules = [ 'service_prep_time' ]
      }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      const endpoint = `/clinics/${companySlug}/appointments/${this.selectedAppointment.id}`
      this.$axios.patch(endpoint, data)
        .then(resp => {
          this.updateActivityLog(endpoint, data, resp, 'PATCH', this.selectedAppointment.id)
          this.closeResetDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data?.detail) {
            message = err.response.data.detail
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    closeResetDialog () {
      this.showDialog = false
      this.$emit('reset')
    },
    closeDialog () {
      this.showDialog = false
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    copyText (text) {
      let container = this.$refs.copy
      this.$copyText(text, container)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.content_copied_to_clipboard')])
        })
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'appointment',
        activity: 'Edit appointment details',
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  },
  destroyed () {
    if (this.skipReset === undefined) {
      this.$store.commit('clinic/resetTempAppointment')
    }
  }
}
</script>

<style scoped>
</style>